/**
 * ExcelParser.js
 *
 * Lee y escribe archivos .xlsx SIN dependencias nuevas.
 *
 * - Lectura: un .xlsx es un ZIP con XML dentro. Se descomprime con `jszip`
 *   (ya presente en el proyecto como dependencia transitiva y fijada en el
 *   package-lock) y el XML se parsea con `DOMParser` (nativo del navegador).
 * - Escritura (plantilla y errores): se usa `write-excel-file`, que el proyecto
 *   ya utiliza para exportar reportes.
 *
 * Gotcha clave del formato .xlsx: las celdas vacías NO aparecen en el XML.
 * Por eso cada celda se ubica por su referencia de columna (atributo `r`, p.ej.
 * "B2") y no por su posición secuencial; de lo contrario las columnas se
 * recorrerían cuando una fila trae huecos.
 */

import JSZip from 'jszip';
import writeXlsxFile from 'write-excel-file';

/**
 * Headers the file MUST declare. `empresa` is required — it auto-fills the
 * company selector. `grupo` does NOT go in the file — always selected on-screen.
 */
const EXPECTED_HEADERS = [
    'empresa', 'nombre', 'apellidos', 'correo',
    'membresia', 'plazo', 'programa', 'addon',
    'atributo 1', 'atributo 2', 'atributo 3'
];

// Map friendly column names (from the template) to internal field names.
const HEADER_ALIASES = {
    'membresia': 'membresia_sku',
    'plazo': 'plazo_sku',
    'addon': 'addon_sku',
    'programa': 'programa_id',
    'atributo 1': 'tag1',
    'atributo 2': 'tag2',
    'atributo 3': 'tag3',
};

export { EXPECTED_HEADERS, HEADER_ALIASES };

/** Case, accent and whitespace insensitive comparison key. */
function normalize(value) {
    return String(value == null ? '' : value)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
}

/** Trim any cell value to a clean string (mirrors old CSV text behavior). */
export function cellToString(value) {
    if (value == null) return '';
    return String(value).trim();
}

// ─────────────────────────────────────────────────────────────
// .xlsx reading (jszip + DOMParser) — no external xlsx library
// ─────────────────────────────────────────────────────────────

/** Convert a cell reference like "B2" to a zero-based column index (B -> 1). */
function columnRefToIndex(ref) {
    const letters = String(ref || '').replace(/[0-9]/g, '').toUpperCase();
    let index = 0;
    for (let i = 0; i < letters.length; i++) {
        index = index * 26 + (letters.charCodeAt(i) - 64); // 'A' = 65
    }
    return index - 1; // zero-based
}

/**
 * Read a .xlsx File into a matrix of rows (each row = array of string cells).
 * The first row is the header row. Empty cells are filled with '' so column
 * positions always line up with the header.
 *
 * @param {File} file
 * @returns {Promise<string[][]>}
 */
export async function readXlsx(file) {
    const zip = await JSZip.loadAsync(file);

    // Shared strings: text cells reference an entry here by index.
    const sharedStrings = [];
    const sharedFile = zip.file('xl/sharedStrings.xml');
    if (sharedFile) {
        const xml = await sharedFile.async('string');
        const doc = new DOMParser().parseFromString(xml, 'application/xml');
        doc.querySelectorAll('si').forEach(si => {
            // A shared string may be split across several <t> runs; join them.
            const parts = si.querySelectorAll('t');
            let text = '';
            parts.forEach(t => { text += t.textContent; });
            sharedStrings.push(text);
        });
    }

    // First worksheet. Workbooks name it sheet1.xml in the vast majority of
    // cases; fall back to the first file under xl/worksheets/ otherwise.
    let sheetFile = zip.file('xl/worksheets/sheet1.xml');
    if (!sheetFile) {
        const candidates = zip.file(/^xl\/worksheets\/.*\.xml$/);
        sheetFile = candidates && candidates.length ? candidates[0] : null;
    }
    if (!sheetFile) {
        throw new Error('El archivo no contiene una hoja de cálculo válida.');
    }

    const sheetXml = await sheetFile.async('string');
    const sheetDoc = new DOMParser().parseFromString(sheetXml, 'application/xml');

    const matrix = [];
    sheetDoc.querySelectorAll('sheetData > row').forEach(rowEl => {
        const cells = rowEl.querySelectorAll('c');
        const rowArr = [];
        cells.forEach(cellEl => {
            const ref = cellEl.getAttribute('r');            // e.g. "B2"
            const type = cellEl.getAttribute('t');           // "s" = shared string
            const colIdx = ref ? columnRefToIndex(ref) : rowArr.length;

            const vEl = cellEl.querySelector('v');
            const isEl = cellEl.querySelector('is > t');     // inline string
            let value = '';
            if (type === 's' && vEl) {
                const idx = parseInt(vEl.textContent, 10);
                value = sharedStrings[idx] != null ? sharedStrings[idx] : '';
            } else if (isEl) {
                value = isEl.textContent;
            } else if (vEl) {
                value = vEl.textContent;
            }

            // Fill any skipped (empty) columns so indexes match the header row.
            while (rowArr.length < colIdx) rowArr.push('');
            rowArr[colIdx] = value;
        });
        matrix.push(rowArr);
    });

    return matrix;
}

// ─────────────────────────────────────────────────────────────
// Row parsing / validation (same business rules as before)
// ─────────────────────────────────────────────────────────────

/**
 * Parse the matrix produced by readXlsx into row objects.
 * `sheet[0]` = headers. Each subsequent array = data row.
 *
 * @param {string[][]} sheet
 * @param {{ companyName?: string }} [selection]
 * @returns {{ headers: string[], rows: object[], errors: string[] }}
 */
export function parseExcel(sheet, selection = {}) {
    if (!Array.isArray(sheet) || sheet.length < 2) {
        return { headers: [], rows: [], errors: ['El archivo no tiene filas de datos'] };
    }

    const headers = (sheet[0] || []).map(h => cellToString(h).toLowerCase());

    const missingHeaders = EXPECTED_HEADERS.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
        return { headers, rows: [], errors: [`Columnas faltantes: ${missingHeaders.join(', ')}`] };
    }

    const rows = [];
    const errors = [];

    for (let i = 1; i < sheet.length; i++) {
        const cells = sheet[i] || [];
        const isEmpty = cells.every(c => cellToString(c) === '');
        if (isEmpty) continue;

        const row = {};
        headers.forEach((h, idx) => {
            const field = HEADER_ALIASES[h] || h;
            row[field] = cellToString(cells[idx]);
        });
        row.row_number = i;

        // Basic front-end validations (same rules as the manual form in Details.jsx)
        if (!row.empresa) errors.push(`Fila ${i}: empresa es requerido`);
        if (!row.nombre) errors.push(`Fila ${i}: nombre es requerido`);
        if (!row.apellidos) errors.push(`Fila ${i}: apellidos es requerido`);
        if (!row.correo) {
            errors.push(`Fila ${i}: correo es requerido`);
        } else if (/\s/.test(row.correo)) {
            errors.push(`Fila ${i}: el correo no debe contener espacios`);
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.correo)) {
            errors.push(`Fila ${i}: correo inválido`);
        }
        if (!row.membresia_sku) errors.push(`Fila ${i}: membresía es requerida`);
        if (!row.plazo_sku) errors.push(`Fila ${i}: plazo es requerido`);

        // Addon: taken as-is from the Excel. It is validated against the catalog
        // later (validateAgainstCatalogs); there is no plazo-based restriction.

        // Attribute uniqueness: tag1, tag2, tag3 must not repeat between each other.
        const tags = [row.tag1, row.tag2, row.tag3].filter(Boolean);
        const uniqueTags = new Set(tags);
        if (tags.length !== uniqueTags.size) {
            errors.push(`Fila ${i}: los atributos (atributo 1, atributo 2, atributo 3) no deben repetirse entre sí`);
        }

        // Confirmation: if `empresa` is in the file AND the company was already
        // resolved/selected, ensure they match. This catches "wrong file" mistakes.
        if (row.empresa && selection.companyName &&
            normalize(row.empresa) !== normalize(selection.companyName)) {
            errors.push(
                `Fila ${i}: la empresa del archivo ("${row.empresa}") no coincide con la ` +
                `empresa seleccionada ("${selection.companyName}")`
            );
        }

        rows.push(row);
    }

    // Check duplicate emails within the file
    const emails = rows.map(r => r.correo.toLowerCase()).filter(Boolean);
    const duplicates = emails.filter((e, i) => emails.indexOf(e) !== i);
    if (duplicates.length > 0) {
        errors.push(`Correos duplicados en el archivo: ${[...new Set(duplicates)].join(', ')}`);
    }

    return { headers, rows, errors };
}

// ─────────────────────────────────────────────────────────────
// .xlsx writing (template + error report) via write-excel-file
// ─────────────────────────────────────────────────────────────

// Canonical bulk-upload template, hosted in S3. This is the single source of
// truth for the template format — do NOT regenerate it locally, so the file the
// user downloads always matches what the parser/validator expects.
export const BULK_TEMPLATE_URL =
    'https://point-files.s3.us-west-2.amazonaws.com/admin-talisis/plantilla_carga_masiva_b2b.xlsx';

/**
 * Download the official .xlsx template hosted in S3.
 *
 * Fetches the file as a blob and triggers a programmatic download so the saved
 * file keeps the expected name. If the fetch fails (e.g. S3 CORS blocks it), we
 * fall back to opening the URL directly so the download still works.
 */
export async function downloadExcelTemplate(fileName = 'plantilla_carga_masiva_b2b.xlsx') {
    try {
        const response = await fetch(BULK_TEMPLATE_URL);
        if (!response.ok) {
            throw new Error(`S3 responded ${response.status}`);
        }
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
        // Fallback for CORS or network issues: let the browser handle the URL.
        window.open(BULK_TEMPLATE_URL, '_blank', 'noopener');
    }
}

/**
 * Generate and download an .xlsx with the error rows.
 * @param {string[]} headers - Original file headers
 * @param {object[]} errorRows - Rows with an 'error' field appended
 * @param {string} [fileName]
 */
export async function downloadErrorExcel(headers, errorRows, fileName = 'errores_carga_masiva.xlsx') {
    const outHeaders = [...headers, 'error'];
    const headerRow = outHeaders.map(h => ({ value: h, fontWeight: 'bold' }));

    const dataRows = errorRows.map(row => {
        const values = headers.map(h => {
            const field = HEADER_ALIASES[h] || h;
            const v = row[field] != null ? row[field] : row[h];
            return { value: v != null ? String(v) : '', type: String };
        });
        values.push({ value: row.error != null ? String(row.error) : '', type: String });
        return values;
    });

    await writeXlsxFile([headerRow, ...dataRows], { fileName });
}
